// ═══════════════════════════════════════════════════════════════════
// Roles & Permissions — the ONE configurable RBAC source (P2-g).
//
// Mirrors the tenant roles-permissions matrix: rows = permission keys grouped
// ("Workspaces" → view.sales/ops/audit, "Capabilities" → mutate), columns = the
// system roles. A super_admin caller toggles any cell (optimistic PATCH, revert
// on error); everyone else sees the matrix read-only.
//
// This data governs workspace VISIBILITY + the canMutate UI convenience ONLY.
// It is NOT a security boundary: every state-changing route on the server stays
// gated by requireAdmin / user_profiles.is_admin. The super_admin column is
// locked here to match the server's self-lockout guard (it must retain mutate).
//
// REVERSIBILITY: when GET /roles is empty (unmigrated / read error) the grid
// shows an info state and the app keeps falling back to the hardcoded
// WORKSPACE_ACCESS + isAdmin model — so this screen is net-zero until edited.
// ═══════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader, Card, Mono } from '@/components/shared/primitives';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { toast } from '@/components/shared/ToastProvider';
import { apiRequest } from '@/lib/supabase';
import { usePersona } from '@/context/PersonaProvider';
import { PERSONA, type RolePermissions } from '@/lib/persona';

const AUDIT = PERSONA.audit.accent;

interface RoleRow {
  id: string;
  key: string;
  label: string;
  is_system: boolean;
  permissions: RolePermissions;
}

// Permission keys the matrix governs, grouped for the row sections. Kept in sync
// with the seed in supabase/migrations/29_role_permissions.sql and lib/rbac.ts.
const PERMISSION_GROUPS: { group: string; keys: { key: string; label: string; hint: string }[] }[] = [
  {
    group: 'Workspaces',
    keys: [
      { key: 'view.sales', label: 'Sales workspace', hint: 'Revenue & growth surfaces' },
      { key: 'view.ops', label: 'Operations workspace', hint: 'Fleet & security surfaces' },
      { key: 'view.audit', label: 'Audit workspace', hint: 'Compliance & evidence (read-only)' },
    ],
  },
  {
    group: 'Capabilities',
    keys: [
      { key: 'mutate', label: 'Write / mutate actions', hint: 'Surfaces write controls (server still enforces is_admin)' },
    ],
  },
];

// Canonical column order (matches user_profiles.role CHECK); unknown/custom roles
// trail in DB order.
const ROLE_ORDER = ['super_admin', 'admin', 'owner', 'billing', 'member', 'viewer'];
function orderRoles(rows: RoleRow[]): RoleRow[] {
  return [...rows].sort((a, b) => {
    const ai = ROLE_ORDER.indexOf(a.key);
    const bi = ROLE_ORDER.indexOf(b.key);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.key.localeCompare(b.key);
  });
}

export function RolesPermissionsPage() {
  const { user } = usePersona();
  const canEdit = user?.role === 'super_admin';

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyCell, setBusyCell] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await apiRequest<{ data: RoleRow[] }>('/roles');
      setRoles(orderRoles(res.data ?? []));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // super_admin column is locked (must retain access; server rejects stripping
  // its mutate). All other roles are editable by a super_admin caller.
  const cellEditable = (role: RoleRow) => canEdit && role.key !== 'super_admin';

  async function toggle(role: RoleRow, permKey: string) {
    if (!cellEditable(role)) return;
    const cell = `${role.key}:${permKey}`;
    setBusyCell(cell);

    const prevPerms = role.permissions ?? {};
    const nextPerms: RolePermissions = { ...prevPerms, [permKey]: !prevPerms[permKey] };

    // Optimistic local update.
    setRoles((rs) => rs.map((r) => (r.key === role.key ? { ...r, permissions: nextPerms } : r)));

    try {
      const updated = await apiRequest<RoleRow>(`/roles/${role.key}/permissions`, {
        method: 'PATCH',
        body: JSON.stringify({ permissions: nextPerms }),
      });
      // Reconcile with the server's canonical row.
      setRoles((rs) => rs.map((r) => (r.key === role.key ? { ...r, permissions: updated.permissions ?? nextPerms } : r)));
    } catch (e) {
      // Revert on error.
      setRoles((rs) => rs.map((r) => (r.key === role.key ? { ...r, permissions: prevPerms } : r)));
      toast({ kind: 'error', title: 'Change not saved', body: e instanceof Error ? e.message : 'Could not update the permission.' });
    } finally {
      setBusyCell(null);
    }
  }

  if (error) return <div style={{ padding: 24 }}><AlertBanner kind="error">{error}</AlertBanner></div>;
  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading roles…</div>;

  const empty = roles.length === 0;

  return (
    <div>
      <PageHeader
        title="Roles & Permissions"
        subtitle={canEdit
          ? 'Configure which workspaces each role sees and whether it may mutate. One source of truth for RBAC visibility.'
          : 'The permission matrix for every role. Editing is limited to super admins.'}
      />

      {!canEdit && (
        <div style={{ marginBottom: 16 }}>
          <AlertBanner kind="info">
            You are viewing the permission matrix read-only. Only a super admin can change role permissions.
          </AlertBanner>
        </div>
      )}

      {empty ? (
        <AlertBanner kind="info">
          No configurable roles are present yet — the app is running on its built-in defaults
          (super admin &amp; admin see all workspaces; every other role is scoped, and writes require an admin).
          Once the roles data is seeded, this matrix governs workspace visibility.
        </AlertBanner>
      ) : (
        <>
          <Card style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thBase, textAlign: 'left', minWidth: 240, position: 'sticky', left: 0, background: 'var(--bg-muted)', zIndex: 1 }}>
                      Permission
                    </th>
                    {roles.map((role) => (
                      <th key={role.key} style={{ ...thBase, textAlign: 'center', minWidth: 116 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600, textTransform: 'none', letterSpacing: 0, fontSize: 13 }}>{role.label || role.key}</span>
                          <Mono style={{ color: 'var(--text-muted)', fontSize: 10.5 }}>{role.key}</Mono>
                          {role.key === 'super_admin' && (
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <Icon name="lock" size={10} /> locked
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSION_GROUPS.map((grp) => (
                    <React.Fragment key={grp.group}>
                      <tr>
                        <td colSpan={roles.length + 1} style={{ padding: '9px 16px', background: 'var(--bg-muted)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-secondary)' }}>
                          {grp.group}
                        </td>
                      </tr>
                      {grp.keys.map((perm) => (
                        <tr key={perm.key}>
                          <td style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1 }}>
                            <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{perm.label}</div>
                            <Mono style={{ color: 'var(--text-muted)', fontSize: 10.5 }}>{perm.key}</Mono>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{perm.hint}</div>
                          </td>
                          {roles.map((role) => {
                            const on = role.permissions?.[perm.key] === true;
                            const editable = cellEditable(role);
                            const cell = `${role.key}:${perm.key}`;
                            const busy = busyCell === cell;
                            return (
                              <td key={role.key} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                                <PermCell on={on} editable={editable} busy={busy} onToggle={() => toggle(role, perm.key)} />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Legend */}
          <Card style={{ marginTop: 16, padding: '14px 18px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20 }}>
              <Legend swatch={<PermGlyph on />} label="Permitted" />
              <Legend swatch={<PermGlyph on={false} />} label="Denied" />
              {canEdit && <Legend swatch={<span style={{ display: 'inline-flex' }}><Icon name="lock" size={15} color="var(--text-muted)" /></span>} label="Super admin — locked (retains full access)" />}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, lineHeight: 1.5 }}>
              These keys govern workspace <strong>visibility</strong> and whether write controls are <strong>shown</strong>.
              They are a convenience layer — the server still enforces every mutation via its admin check, so this matrix
              can never grant access the backend would deny. The Audit workspace is read-only for everyone regardless of the
              <Mono style={{ margin: '0 3px' }}>mutate</Mono> flag.
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ── Cell ─────────────────────────────────────────────────────────────────────
function PermCell({ on, editable, busy, onToggle }: { on: boolean; editable: boolean; busy: boolean; onToggle: () => void }) {
  if (!editable) return <PermGlyph on={on} />;
  return (
    <button
      onClick={onToggle}
      disabled={busy}
      title={on ? 'Click to revoke' : 'Click to grant'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30,
        borderRadius: 8, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
        border: `1px solid ${on ? AUDIT : 'var(--border-strong)'}`,
        background: on ? 'var(--ps-pill-violet-bg, var(--accent-light))' : 'var(--surface)',
        color: on ? AUDIT : 'var(--text-muted)', transition: 'background .12s, border-color .12s',
      }}
    >
      <Icon name={on ? 'check' : 'minus'} size={16} />
    </button>
  );
}

function PermGlyph({ on }: { on: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%',
      background: on ? 'var(--ps-pill-green-bg)' : 'var(--ps-pill-red-bg, #FEE2E2)',
      color: on ? 'var(--ps-pill-green-tx)' : 'var(--ps-pill-red-tx, #B91C1C)',
    }}>
      <Icon name={on ? 'check' : 'x'} size={15} />
    </span>
  );
}

function Legend({ swatch, label }: { swatch: React.ReactNode; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)' }}>
      {swatch}
      {label}
    </span>
  );
}

const thBase: React.CSSProperties = {
  padding: '12px 16px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em',
  color: 'var(--text-muted)', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};

export default RolesPermissionsPage;
